import { type ThemeProps } from '@kijiji/theme'
import styled from 'styled-components'

import { PrevNextBtn } from '@/components/shared/carousel-buttons/styled'
import { ImageCustom } from '@/components/shared/image'
import { ImageYAMS } from '@/components/shared/image/ImageYAMS'
import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { zIndexRegistry } from '@/constants/zIndexRegistry'
import { Badge } from '@/ui/atoms/badge'
import { BodyText } from '@/ui/atoms/body-text'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { MEDIA_QUERIES } from '@/ui/constants/mediaQueries'
import { hexToRgb } from '@/ui/helpers/hexToRgb'

type RichCardContainerProps = {
  $isHighlight: boolean
  $isHorizontalLayout: boolean
  $isCompact: boolean
}

export const RichCardContainer = styled.article<RichCardContainerProps>(
  ({ theme, $isHighlight, $isHorizontalLayout }) => `
  height: 100%;
  dispay: flex;
  border-radius: ${theme.borderRadius.medium}; 
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  max-width: 100%;


  ${MEDIA_QUERIES(theme).small} {
    flex-direction: ${$isHorizontalLayout ? 'row' : 'column'};
  }

  ${
    $isHighlight
      ? `
    background-color: ${theme.colors.yellow.light5};
    border: 0.15rem solid ${theme.colors.yellow.primary};
    `
      : `
    background-color: ${theme.colors.white};
    border: 0.1rem solid ${theme.colors.grey.light4};
    `
  }

  ${PrevNextBtn} {
    transition: opacity 0.2s ease;
    opacity: 0;

    &:focus-visible {
      opacity: 0.7;
    }
  }

  &:hover {
    box-shadow: ${theme.boxShadow['shadow-4']}
  }

  &:hover,
  &:focus-visible {
    ${PrevNextBtn} {
      opacity: 0.7;
    }
  }

`
)

type RichCardWrapperProps = {
  $isHorizontalLayout: boolean
}

export const RichCardWrapper = styled.div<RichCardWrapperProps>(
  ({ theme, $isHorizontalLayout }) => `
    display: flex;
    flex: 1 1 0%;
    flex-direction: column;

    ${MEDIA_QUERIES(theme).small} {
      ${
        $isHorizontalLayout
          ? `
          flex-direction: row;
          justify-content: space-between;
          gap: 0.1rem;
        `
          : ``
      }
    }
  `
)

type RichCardContentWrapperProps = {
  $isHorizontalLayout: boolean
}

export const RichCardContentWrapper = styled.div<RichCardContentWrapperProps>(
  ({ theme, $isHorizontalLayout }) => `
  display: flex;
  flex-direction: column;
  min-width: 0;
  order: 2;

  ${MEDIA_QUERIES(theme).small} {
    ${$isHorizontalLayout && `width: 60%;`}
  }
  `
)

// Header

export const RichCardHeaderContainer = styled.header(
  ({ theme }) => `
  display: flex;
  flex-direction: column-reverse;
  gap: ${theme.spacing.defaultSmall};
  padding: ${theme.spacing.defaultSmall};
`
)

export const RichCardTitleWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const RichCardTitle = styled(HeadlineText)(
  ({ theme }) => `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 1.6rem;
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.grey.primary};
`
)

// eslint-disable-next-line @kijiji/prefer-link-custom
export const RichCardLink = styled.a(
  ({ theme }) => `
  text-decoration: initial;
  color: ${theme.colors.grey.primary};
  text-decoration: none;
  
  &:focus-visible {
    outline-offset: -0.1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: ${zIndexRegistry.CARD};
  }

  &:visited {
    color: ${theme.colors.violet.primary};
  }
  `
)

// Content

export const RichCardContentContainer = styled.main(
  ({ theme }) => `
  padding: 0 ${theme.spacing.defaultSmall} ${theme.spacing.defaultSmall};
  `
)

// Media

type RichCardMediaWrapperProps = {
  $isHorizontalLayout: boolean
}

export const RichCardMediaWrapper = styled.div<RichCardMediaWrapperProps>(
  ({ theme, $isHorizontalLayout }) => `
  position: relative;
  order: 1;
  width: 100%;
  max-height: 24rem;

  ${MEDIA_QUERIES(theme).small} {
    ${$isHorizontalLayout && `width: 40%;`}
  }
  `
)

export const RichCardImagePanelContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

export const RichCardImageLinkWrapper = styled(LinkCustom)`
  display: block;
  width: 100%;
  height: 100%;

  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.blue.primary};
    outline-offset: -0.2rem;
  }
`

type RichCardImageProps = {
  $imageContain?: boolean
}

export const RichCardImage = styled(ImageYAMS)<RichCardImageProps>(
  ({ theme, $imageContain }) => `
  max-width: 100%;
  max-height: 24rem;
  width: 100%;
  aspect-ratio: 360 / 240;
  object-fit: ${$imageContain ? 'contain' : 'cover'};
  background-color: ${$imageContain ? theme.colors.white : 'transparent'};
  display: block;
`
)

export const RichCardImageOverlayContainer = styled.div(
  ({ theme }) => `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${hexToRgb(theme.colors.grey.primary, 0.6)};
  color: ${theme.colors.white};
`
)

export const RichCardImageOverlayWrapper = styled.div(
  ({ theme }) => `
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 0 ${theme.spacing.giant};
    gap: ${theme.spacing.defaultSmall};
    align-items: center;
  `
)

export const RichCardImageOverlayTitle = styled(HeadlineText)(
  ({ theme }) => `
  && {
    // Truncate title on the second line
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    overflow: hidden;
    font-size: ${theme.typography.body.xSmall.fontSize};
    line-height: ${theme.typography.body.xSmall.lineHeight};
    text-align: center;
  }
`
)

export const RichCardImageOverlayDescription = styled(BodyText)`
  && {
    // Truncate description on the third line
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    display: -webkit-box;
    overflow: hidden;
    text-align: center;
  }
`

// Carousel

export const RichCardCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  z-index: ${zIndexRegistry.CARD_CAROUSEL};
`

// Badges

export const AdTypeBadge = styled(Badge)(
  ({ theme }) => `
  top: ${theme.spacing.defaultSmall};
  left: ${theme.spacing.defaultSmall};
  position: absolute;
  z-index: ${zIndexRegistry.CARD_ELEMENTS};
`
)

export const FavouriteButtonContainer = styled.div(
  ({ theme }) => `
  position: absolute;
  top: ${theme.spacing.defaultSmall};
  right: ${theme.spacing.defaultSmall};
  z-index: ${zIndexRegistry.CARD_ELEMENTS};
`
)

// Footer

export const StyledRichCardFooter = styled.div(
  ({ theme, $isHighlight }: { theme: ThemeProps; $isHighlight: boolean }) => `
  
  padding: ${theme.spacing.defaultSmall} 0;
  margin: 0 ${theme.spacing.defaultSmall};
  min-height: 5rem;
  display: flex;
  order: 3;

  ${
    $isHighlight
      ? `
    border-top: 1px solid ${theme.colors.yellow.light4};
    `
      : ` 
    border-top: 1px solid ${theme.colors.grey.light4};
    `
  }
`
)

export const RichCardLogoContainer = styled.div`
  height: 3.2rem;
`

export const RichCardLogo = styled(ImageCustom)(
  ({ theme }) => `
  border-radius: ${theme.borderRadius.small};
  max-height: 3.2rem;
  max-width: 6rem;
  object-fit: contain;
`
)
